// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Global State (Zustand)
// Manages UI state: sidebar, active project, notifications, etc.
// Server state is managed by TanStack Query in hooks/
// ─────────────────────────────────────────────────────────────────────────────
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Notification ──────────────────────────────────────────────────────────────

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
}

// ── Store interface ───────────────────────────────────────────────────────────

interface AppStore {
  // Sidebar
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  // Active project context
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;

  // Active scenario tab
  activeScenarioTab: 'calculator' | 'results' | 'scenarios' | 'report';
  setActiveScenarioTab: (tab: AppStore['activeScenarioTab']) => void;

  // Notifications
  notifications: Notification[];
  addNotification: (n: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  notify: (type: NotificationType, title: string, message?: string) => void;

  // Preferences (persisted)
  defaultDiscountRate: number;
  setDefaultDiscountRate: (rate: number) => void;

  defaultAnalysisPeriod: number;
  setDefaultAnalysisPeriod: (period: number) => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Sidebar
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

      // Active project
      activeProjectId: null,
      setActiveProjectId: (id) => set({ activeProjectId: id }),

      // Tabs
      activeScenarioTab: 'calculator',
      setActiveScenarioTab: (tab) => set({ activeScenarioTab: tab }),

      // Notifications
      notifications: [],
      addNotification: (n) => {
        const id = Math.random().toString(36).slice(2);
        set((s) => ({ notifications: [...s.notifications, { ...n, id }] }));
        // Auto-remove after 4 seconds
        setTimeout(() => get().removeNotification(id), 4000);
      },
      removeNotification: (id) =>
        set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
      notify: (type, title, message) =>
        get().addNotification({ type, title, message }),

      // Preferences
      defaultDiscountRate: 5,
      setDefaultDiscountRate: (rate) => set({ defaultDiscountRate: rate }),

      defaultAnalysisPeriod: 25,
      setDefaultAnalysisPeriod: (period) => set({ defaultAnalysisPeriod: period }),
    }),
    {
      name: 'lcca-nexus-prefs',
      partialize: (s) => ({
        defaultDiscountRate: s.defaultDiscountRate,
        defaultAnalysisPeriod: s.defaultAnalysisPeriod,
        sidebarOpen: s.sidebarOpen,
      }),
    }
  )
);
